import { Component, inject, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import JSON5 from 'json5';

import { SasSelectEmptyValueDirective, SasTruncateSelectOptionsDirective } from '@directives';
import { Lookup } from '@interfaces';
import { FormSubmittedService, LookupUseCacheService } from '@services';
import { SasDynamicFormService } from '../sas-dynamic-form.service';

@Component({
    selector: 'sas-select-field-type',
    standalone: true,
    imports: [ReactiveFormsModule, FormlyModule, TranslateModule, SasTruncateSelectOptionsDirective, SasSelectEmptyValueDirective],
    providers: [],
    template: `
        <div class="sas-select-wrapper mb-3">
            <label class="form-label"
                    title="{{field.props.label ?? '' | translate}}"
                    translate="{{field.props.label}}"
                    [class.is-invalid]="hasVisibleError"
                    [for]="instanceId"
                    [hidden]="field.props['hideLabel']"
                    [id]="field.id + 'label'">
            </label>
            @if (field.props.required) {
            <span> *</span>
            }
            <div class="sas-inline-block" [id]="field.id + 'Help'" [title]="helpText">
                @if (helpText?.length) {
                <i aria-hidden="true" class="bi bi-info-circle-fill sas-info-icon"></i>
                }
            </div>
            <select class="form-select sas-select {{field.props['sasClass']}}"
                    sasTruncateSelectOptions
                    tabindex="0"
                    title="{{helpText}}"
                    [attr.aria-describedby]="ariaDescribedBy"
                    [attr.aria-invalid]="hasVisibleError"
                    [attr.aria-labelledby]="field.id + 'label'"
                    [attr.aria-required]="field.props.required"
                    [class.is-invalid]="hasVisibleError"
                    [formControl]="formControl"
                    [id]="instanceId"
                    [name]="field.name"
                    [required]="field.props.required ?? false"
                    [sasEmptyValue]="undefined"
                    (change)="onSelect()">
                <option [ngValue]="undefined">{{ 'SAS_SELECT.DEFAULT_TEXT' | translate }}</option>
                @for (item of sasOptions; track $index) {
                <option [ngValue]="item.id" [title]="item.name | translate">{{item.name | translate}}</option>
                }
            </select>
            @if (field.props['sasHelptext']) {
            <div class="sas-inline-block" title="{{field.props['sasHelptext'] ?? '' | translate}}" [id]="instanceId + 'Desc'">
                <i aria-hidden="true" class="bi bi-info-circle-fill sas-info-icon"></i>
            </div>
            }
            <div aria-atomic="true" class="invalid-feedback" role="alert" [id]="field.id + 'Desc'">
                <small>{{ sasDynamicFormService.getFieldErrorMessage(field) }}</small>
            </div>
        </div>
    `,
    styleUrl: './sas-select-field-type.component.scss'
})
export class SasSelectFieldTypeComponent extends FieldType<FieldTypeConfig> implements OnInit {
    formSubmittedService = inject(FormSubmittedService);
    instanceId = '';
    sasOptions: Lookup[] = [];
    private translate = inject(TranslateService);
    sasDynamicFormService = inject(SasDynamicFormService);
    private lookupUseCacheService = inject(LookupUseCacheService);

    helpText = '';

    constructor() {
        super()
    }

    ngOnInit(): void {
        if (this.field.props['helptext']) {
            const helpText = this.translate.instant(this.field.props['helptext']);

            this.helpText = helpText === this.field.props['helptext'] ? '' : helpText;
        }

        this.instanceId = this.field.id + Math.random().toString(36).substring(2);

        if (this.field.props['dataSource']?.type === 'api') {
            this.lookupUseCacheService.getOptions(this.field.props['dataSource'].url)
                .subscribe((result: Lookup[]) => {
                    this.sasOptions = result;
                    this.sasOptions = this.sortOptions(this.sasOptions);
                    this.field.formControl.setValue(this.field.model[`${this.field.key!}`]);
                });
        }
        else if (this.field.props['dataSource']?.type === 'static'
            && (this.field.props['dataSource']?.source !== '' && this.field.props['dataSource']?.source !== '[]') ) {
            const parsedOptions = JSON5.parse(this.field.props['dataSource'].source);
            const options = JSON.stringify(parsedOptions);  // Convert it back to a standard JSON string
            this.sasOptions = this.sortOptions(JSON.parse(options));
        }
        else {
            this.sasOptions = this.sortOptions(this.field.props['sasOptions']);
        }
    }

    get hasVisibleError(): boolean {
        const control = this.field.formControl;

        return control.invalid && (control.dirty || control.touched || this.formSubmittedService.isSubmitted());
    }

    get ariaDescribedBy(): string | null {
        const ids = [
            this.helpText ? `${this.field.id}Help` : null,
            this.hasVisibleError ? `${this.field.id}Desc` : null
        ].filter(Boolean);

        return ids.length ? ids.join(' ') : null;
    }

    onSelect(): void {
        if (this.field.props['sasOnSelect'] !== undefined) {
            this.field.props['sasOnSelect'].emit({
                'value': this.formControl.value,
                'selectId': this.instanceId,
                'selectName': this.field.name
            });
        }
    }

    sortOptions(options: Lookup[] | null | undefined): Lookup[] {
        return (options ?? []).toSorted((a, b) => (a?.name ?? '').localeCompare(b?.name ?? '', 'en', { numeric: true }));
    }
}
